import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface SlideModalProps {
    open: boolean;
    onClose: () => void;
    title?: React.ReactNode;
    onBack?: () => void;
    maxWidth?: string;
    headerRight?: React.ReactNode;
    children: React.ReactNode;
}

export default function SlideModal({
    open,
    onClose,
    title,
    onBack,
    maxWidth = 'max-w-md',
    headerRight,
    children,
}: SlideModalProps) {
    const [closing, setClosing] = useState(false);

    const handleClose = useCallback(() => {
        setClosing(true);
        setTimeout(() => {
            setClosing(false);
            onClose();
        }, 250);
    }, [onClose]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, handleClose]);

    if (!open) return null;

    return createPortal(
        <>
            <div className="fixed inset-0 z-40 bg-black/30" onClick={handleClose} />

            <div
                className={`fixed inset-y-0 right-0 z-50 flex w-full ${maxWidth} flex-col bg-white shadow-[-4px_0_24px_rgba(0,0,0,0.08)] ${
                    closing ? 'animate-slide-out-right' : 'animate-slide-in-right'
                }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-6">
                    <div className="flex items-center gap-3">
                        {/* <button
                            type="button"
                            title="Back"
                            onClick={onBack ?? handleClose}
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#eee] text-[#555] transition-colors hover:bg-[#f5f5f5]"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button> */}
                        {title &&
                            (typeof title === 'string' ? (
                                <h2 className="text-[15px] font-bold text-secondary">{title}</h2>
                            ) : (
                                title
                            ))}
                    </div>
                    <div className="flex items-center gap-2">
                        {headerRight}
                        <button
                            type="button"
                            title="Close"
                            onClick={handleClose}
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#eee] text-[#555] transition-colors hover:bg-[#f5f5f5]"
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Body */}
                {children}
            </div>
        </>,
        document.body,
    );
}
